import { createElement, Fragment } from 'react';
import type { ReactNode } from 'react';

/**
 * Đổi HTML do Admin soạn (Quy tắc cộng đồng) thành phần tử React theo danh sách cho phép.
 *
 * Trang công khai nên KHÔNG dùng dangerouslySetInnerHTML: DOMParser tạo một tài liệu trơ (không
 * chạy script, không tải ảnh), rồi chỉ những thẻ dưới đây được dựng lại, bỏ mọi thuộc tính trừ
 * href (http/https/mailto/tel) và src ảnh (https). Thẻ lạ thì giữ chữ bên trong, thẻ nguy hiểm
 * (script, style, iframe, form...) bỏ cả nội dung.
 */

const TAGS: Record<string, string> = {
  p: 'p',
  div: 'div',
  section: 'div',
  article: 'div',
  br: 'br',
  hr: 'hr',
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  u: 'u',
  s: 's',
  del: 's',
  small: 'small',
  sub: 'sub',
  sup: 'sup',
  blockquote: 'blockquote',
  ul: 'ul',
  ol: 'ol',
  li: 'li',
  // Trang đã có h1; nội dung dùng h3/h4 để không phá thứ bậc tiêu đề.
  h1: 'h3',
  h2: 'h3',
  h3: 'h3',
  h4: 'h4',
  h5: 'h4',
  h6: 'h4',
  a: 'a',
  img: 'img',
};

const DROP = new Set([
  'script',
  'style',
  'template',
  'noscript',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'link',
  'meta',
  'base',
  'title',
  'head',
  'svg',
  'math',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'video',
  'audio',
  'canvas',
]);

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:)/i;
const SAFE_SRC = /^https:\/\//i;
const MAX_DEPTH = 40;

function convert(node: Node, key: string, depth: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (DROP.has(tag)) return null;

  const children =
    depth >= MAX_DEPTH
      ? element.textContent
      : Array.from(element.childNodes).map((child, index) =>
          convert(child, `${key}.${index}`, depth + 1)
        );
  const mapped = TAGS[tag];

  if (!mapped) return <Fragment key={key}>{children}</Fragment>;
  if (mapped === 'br' || mapped === 'hr') return createElement(mapped, { key });

  if (mapped === 'a') {
    const href = (element.getAttribute('href') || '').trim();
    if (!SAFE_HREF.test(href)) return <Fragment key={key}>{children}</Fragment>;
    const external = /^https?:/i.test(href);
    return (
      <a
        key={key}
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }

  if (mapped === 'img') {
    const src = (element.getAttribute('src') || '').trim();
    if (!SAFE_SRC.test(src)) return null;
    return (
      <img
        key={key}
        src={src}
        alt={element.getAttribute('alt') || ''}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    );
  }

  return createElement(mapped, { key }, children);
}

const HAS_TAG = /<[a-z!/][\s\S]*>/i;

/** HTML (hoặc chữ thuần, xuống dòng giữ nguyên) → ReactNode an toàn. Rỗng thì trả null. */
export function htmlToReact(html: string): ReactNode {
  const source = (html || '').trim();
  if (!source) return null;

  if (!HAS_TAG.test(source)) {
    // Chữ thuần: mỗi đoạn cách nhau một dòng trống là một <p>, xuống dòng đơn thành <br>.
    return source.split(/\n\s*\n/).map((paragraph, index) => (
      <p key={index}>
        {paragraph.split('\n').map((line, lineIndex) => (
          <Fragment key={lineIndex}>
            {lineIndex > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </p>
    ));
  }

  try {
    const doc = new DOMParser().parseFromString(source, 'text/html');
    return Array.from(doc.body.childNodes).map((child, index) => convert(child, String(index), 0));
  } catch {
    return null;
  }
}
